import { Box } from '@primer/react';
import { ArrowLeftIcon, ArrowRightIcon, DownloadIcon, FileDiffIcon, SignInIcon } from '@primer/octicons-react';

import React, { useEffect, useMemo, useState } from 'react';
import { financeHelperApi } from '../../../api/financeHelperApi';
import { hinhThucNopBomApi } from '../../../api/hinhThucNopBomApi';
import { IHocSinh } from '../../../models/response/crm-hocsinh/IHocsinh';
import { IHinhThucNopBom } from '../../../models/response/finance/IHinhThucNopBom';
import { IKhoanNopDistinct, ITinhBaoPhiKhoanNop, ITinhBaoPhiKhoanResponse } from '../../../models/response/finance/ITinhBaoPhi';
import Button from '../../../components/ui/button';
import * as XLSX from 'xlsx';

interface LichSuBaoPhiProps {
    hocSinh: IHocSinh;
    hinhThucNopBomSelectedIds: number[];
    onSubmit: () => void;
    onBack: () => void;
    onCancel: () => void;
    sf_tinhbaophi_id: number;
}

const KetQuaBaoPhi: React.FC<LichSuBaoPhiProps> = ({ hocSinh, hinhThucNopBomSelectedIds, onSubmit, onBack, onCancel, sf_tinhbaophi_id }) => {
    const [hinhThucNopBoms, setHinhThucNopBoms] = useState<IHinhThucNopBom[]>([]);
    const [filteredHinhThucNopBoms, setFilteredHinhThucNopBoms] = useState<IHinhThucNopBom[]>([]);
    const [tinhBaoPhiKhoanResponses, setTinhBaoPhiKhoanResponses] = useState<ITinhBaoPhiKhoanResponse>();

    useEffect(() => {
        handleReloadHinhThucHinhThucNopBom();
        handleReloadDataBaoPhi();
    }, []);
    useEffect(() => {
        const filtered = hinhThucNopBoms.filter((item) =>
            hinhThucNopBomSelectedIds.includes(item.id)
        );
        setFilteredHinhThucNopBoms(filtered);
    }, [hinhThucNopBoms, hinhThucNopBomSelectedIds]);

    const handleReloadDataBaoPhi = async () => {
        const res = await financeHelperApi.sf_tinhbaophi_selectbyid(sf_tinhbaophi_id);
        if (res.is_success) {
            setTinhBaoPhiKhoanResponses(res.data.data[0]);
        }
    };

    const handleReloadHinhThucHinhThucNopBom = async () => {
        const res = await hinhThucNopBomApi.selectAll();
        if (res.is_success) {
            setHinhThucNopBoms(res.data);
        }
    };
    const handleViewPdf = async () => {
        if (tinhBaoPhiKhoanResponses) {
            const pdf_file = tinhBaoPhiKhoanResponses.pdf_file;
            if (pdf_file) {
                window.open(pdf_file, '_blank', 'noopener,noreferrer');
            }
        }
    };
    const handleExportTableToExcel = () => {
        setExpandedGroups(new Set(groupNames));
        setTimeout(() => {
            const table = document.getElementById('detail_khoannop_id');
            if (!table) {
                return;
            }
            const worksheet = XLSX.utils.table_to_sheet(table);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');
            XLSX.writeFile(workbook, 'ExportedData.xlsx');
        }, 200);
    };
    const distinctList = useMemo(() => {
        if (!tinhBaoPhiKhoanResponses) return [];

        const viewmodels = tinhBaoPhiKhoanResponses.sf_tinhbaophi_khoannop_viewmodels;

        const distinctMap = new Map<number, IKhoanNopDistinct>();

        viewmodels.forEach((item) => {
            if (!distinctMap.has(item.sf_khoannop_id)) {
                distinctMap.set(item.sf_khoannop_id, {
                    ten_khoan_nop: item.ten_khoan_nop,
                    ma_khoan_nop_group: item.ma_khoan_nop_group,
                    ten_khoan_nop_group: item.ten_khoan_nop_group,
                    sf_khoannop_group_id: item.sf_khoannop_group_id,
                    sf_khoannop_id: item.sf_khoannop_id,
                    ma_khoan_nop: item.ma_khoan_nop,
                });
            }
        });

        return Array.from(distinctMap.values());
    }, [tinhBaoPhiKhoanResponses]);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    const toggleGroup = (groupName: string) => {
        setExpandedGroups((prev) => {
            const newExpandedGroups = new Set(prev);
            if (newExpandedGroups.has(groupName)) {
                newExpandedGroups.delete(groupName);
            } else {
                newExpandedGroups.add(groupName);
            }
            return newExpandedGroups;
        });
    };

    const groupedRows = tinhBaoPhiKhoanResponses?.sf_tinhbaophi_khoannop_viewmodels.reduce(
        (groups, row) => {
            const group = row.ten_khoan_nop_group;
            if (!groups[group]) {
                groups[group] = [];
            }
            groups[group].push(row);
            return groups;
        },
        {} as Record<string, ITinhBaoPhiKhoanNop[]>
    );
    const groupNames = groupedRows ? Object.keys(groupedRows) : [];

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'border.default',
                borderRadius: 2,
                maxHeight: 'calc(100vh - 250px)',
                overflowY: 'auto',
                p: 1,
            }}
        >
            <Box
                sx={{
                    borderBottom: '1px solid',
                    borderColor: 'border.default',
                    pb: 1,
                    height: 'calc(90vh - 290px)',
                    overflowY: 'auto',
                }}
            >
                <Box
                    sx={{
                        overflowX: 'auto',
                    }}
                >
                    <table
                        style={{
                            borderCollapse: 'collapse',
                            fontSize: '13px',
                        }}
                        id='detail_khoannop_id'
                    >
                        <thead>
                            <tr>
                                <th
                                    style={{
                                        border: '1px solid #ddd',
                                        padding: '5px',
                                        textAlign: 'center',
                                        backgroundColor: '#f6f8fa',
                                        minWidth: 500,
                                    }}
                                    colSpan={2}
                                >
                                    Thông tin
                                </th>
                                {filteredHinhThucNopBoms.map((x) => (
                                    <th
                                        key={x.id}
                                        style={{
                                            border: '1px solid #ddd',
                                            textAlign: 'center',
                                            backgroundColor: '#f6f8fa',
                                            minWidth: 300,
                                        }}
                                        colSpan={3}
                                    >
                                        {x.name}
                                    </th>
                                ))}
                            </tr>
                            <tr>
                                <th
                                    style={{
                                        border: '1px solid #ddd',
                                        padding: '5px',
                                        textAlign: 'center',
                                        backgroundColor: '#f6f8fa',
                                        width: 120,
                                        fontWeight: 500,
                                    }}
                                >
                                    Mã khoản nộp
                                </th>
                                <th
                                    style={{
                                        border: '1px solid #ddd',
                                        padding: '5px',
                                        textAlign: 'center',
                                        backgroundColor: '#f6f8fa',
                                        fontWeight: 500,
                                    }}
                                >
                                    Tên khoản nộp
                                </th>
                                {filteredHinhThucNopBoms.flatMap((x) => [
                                    <th
                                        key={`DM_${x.id}`}
                                        style={{
                                            border: '1px solid #ddd',
                                            padding: '5px',
                                            textAlign: 'center',
                                            backgroundColor: '#f6f8fa',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Mức thu
                                    </th>,
                                    <th
                                        key={`MG_${x.id}`}
                                        style={{
                                            border: '1px solid #ddd',
                                            padding: '5px',
                                            textAlign: 'center',
                                            backgroundColor: '#f6f8fa',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Giảm trừ
                                    </th>,
                                    <th
                                        key={`PN_${x.id}`}
                                        style={{
                                            border: '1px solid #ddd',
                                            padding: '5px',
                                            textAlign: 'center',
                                            backgroundColor: '#f6f8fa',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Thành tiền
                                    </th>,
                                ])}
                            </tr>
                        </thead>
                        <tbody>
                            {groupNames.map((groupName) => {
                                const rows = groupedRows ? groupedRows[groupName] : [];
                                return (
                                    <React.Fragment key={groupName}>
                                        <tr
                                            style={{
                                                backgroundColor: '#f6f8fa',
                                                cursor: 'pointer',
                                                fontWeight: 500,
                                            }}
                                            onClick={() => toggleGroup(groupName)}
                                        >
                                            <td
                                                colSpan={2}
                                                style={{
                                                    border: '1px solid #ddd',
                                                    padding: '5px',
                                                    paddingLeft: '20px',
                                                    textAlign: 'left',
                                                    backgroundColor: '#f6f8fa',
                                                    fontWeight: 'bold',

                                                }}
                                            >
                                                {groupName} <span>{expandedGroups.has(groupName) ? '[-]' : '[+]'}</span>
                                            </td>
                                            {filteredHinhThucNopBoms.map((x) => {
                                                const summaryGroup = tinhBaoPhiKhoanResponses?.sf_tinhbaophi_khoannop_viewmodels
                                                    .filter(
                                                        (item) =>
                                                            item.sf_hinhthucnopbom_id === x.id &&
                                                            item.ten_khoan_nop_group === groupName
                                                    )
                                                    .reduce(
                                                        (acc, item) => ({
                                                            so_tien_dinh_muc: acc.so_tien_dinh_muc + item.so_tien_dinh_muc,
                                                            so_tien_mien_giam: acc.so_tien_mien_giam + item.so_tien_mien_giam,
                                                        }),
                                                        { so_tien_dinh_muc: 0, so_tien_mien_giam: 0 }
                                                    );
                                                return [
                                                    <td
                                                        key={`DM_${x.id}`}
                                                        style={{
                                                            border: '1px solid #ddd',
                                                            padding: '5px',
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {summaryGroup?.so_tien_dinh_muc.toLocaleString() || 0}
                                                    </td>,
                                                    <td
                                                        key={`MG_${x.id}`}
                                                        style={{
                                                            border: '1px solid #ddd',
                                                            padding: '5px',
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {summaryGroup?.so_tien_mien_giam.toLocaleString() || 0}
                                                    </td>,
                                                    <td
                                                        key={`PN_${x.id}`}
                                                        style={{
                                                            border: '1px solid #ddd',
                                                            padding: '5px',
                                                            textAlign: 'right',
                                                        }}
                                                    >
                                                        {summaryGroup
                                                            ? (summaryGroup.so_tien_dinh_muc - summaryGroup.so_tien_mien_giam).toLocaleString()
                                                            : 0}
                                                    </td>,
                                                ];
                                                // return (
                                                //     <td
                                                //         key={`header_${x.id}`}
                                                //         style={{
                                                //             border: '1px solid #ddd',
                                                //             padding: '5px',
                                                //             textAlign: 'center',
                                                //             backgroundColor: '#f6f8fa',
                                                //         }}
                                                //     />
                                                // );
                                            })}

                                        </tr>
                                        {expandedGroups.has(groupName) &&
                                            distinctList.filter(x => x.ten_khoan_nop_group == groupName).map((row: IKhoanNopDistinct) => (
                                                <tr key={row.sf_khoannop_id}>
                                                    <td
                                                        style={{
                                                            border: '1px solid #ddd',
                                                            padding: '5px',
                                                            textAlign: 'left',
                                                        }}
                                                    >
                                                        {row.ma_khoan_nop}
                                                    </td>
                                                    <td
                                                        style={{
                                                            border: '1px solid #ddd',
                                                            padding: '5px',
                                                            textAlign: 'left',
                                                        }}
                                                    >
                                                        {row.ten_khoan_nop}
                                                    </td>
                                                    {filteredHinhThucNopBoms.flatMap((x) => {
                                                        const relatedItem = tinhBaoPhiKhoanResponses?.sf_tinhbaophi_khoannop_viewmodels.find(
                                                            (item) => item.sf_hinhthucnopbom_id === x.id && item.sf_khoannop_id == row.sf_khoannop_id && item.sf_khoannop_group_id == row.sf_khoannop_group_id
                                                        );
                                                        return [
                                                            <td
                                                                key={`DM_${row.sf_khoannop_id}_${x.id}`}
                                                                style={{
                                                                    border: '1px solid #ddd',
                                                                    padding: '5px',
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                {relatedItem?.so_tien_dinh_muc.toLocaleString() || 0}
                                                            </td>,
                                                            <td
                                                                key={`MG_${row.sf_khoannop_id}_${x.id}`}
                                                                style={{
                                                                    border: '1px solid #ddd',
                                                                    padding: '5px',
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                {relatedItem?.so_tien_mien_giam.toLocaleString() || 0}
                                                            </td>,
                                                            <td
                                                                key={`PN_${row.sf_khoannop_id}_${x.id}`}
                                                                style={{
                                                                    border: '1px solid #ddd',
                                                                    padding: '5px',
                                                                    textAlign: 'right',
                                                                }}
                                                            >
                                                                {relatedItem
                                                                    ? (relatedItem.so_tien_dinh_muc - relatedItem.so_tien_mien_giam).toLocaleString()
                                                                    : 0}
                                                            </td>,
                                                        ];
                                                    })}
                                                </tr>
                                            ))}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr
                                style={{
                                    backgroundColor: '#FBEEEE',
                                    cursor: 'pointer',
                                    fontWeight: 500,
                                }}
                            >
                                <td
                                    colSpan={2}
                                    style={{
                                        border: '1px solid #ddd',
                                        padding: '5px',
                                        paddingLeft: '20px',
                                        textAlign: 'left',
                                        backgroundColor: '#FBEEEE',
                                        fontWeight: 'bold',

                                    }}
                                >
                                    Tổng tiền
                                </td>
                                {filteredHinhThucNopBoms.map((x) => {
                                    const summaryGroup = tinhBaoPhiKhoanResponses?.sf_tinhbaophi_khoannop_viewmodels
                                        .filter(
                                            (item) =>
                                                item.sf_hinhthucnopbom_id === x.id
                                        )
                                        .reduce(
                                            (acc, item) => ({
                                                so_tien_dinh_muc: acc.so_tien_dinh_muc + item.so_tien_dinh_muc,
                                                so_tien_mien_giam: acc.so_tien_mien_giam + item.so_tien_mien_giam,
                                            }),
                                            { so_tien_dinh_muc: 0, so_tien_mien_giam: 0 }
                                        );
                                    return [
                                        <td
                                            key={`DM_${x.id}`}
                                            style={{
                                                border: '1px solid #ddd',
                                                padding: '5px',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {summaryGroup?.so_tien_dinh_muc.toLocaleString() || 0}
                                        </td>,
                                        <td
                                            key={`MG_${x.id}`}
                                            style={{
                                                border: '1px solid #ddd',
                                                padding: '5px',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {summaryGroup?.so_tien_mien_giam.toLocaleString() || 0}
                                        </td>,
                                        <td
                                            key={`PN_${x.id}`}
                                            style={{
                                                border: '1px solid #ddd',
                                                padding: '5px',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {summaryGroup
                                                ? (summaryGroup.so_tien_dinh_muc - summaryGroup.so_tien_mien_giam).toLocaleString()
                                                : 0}
                                        </td>,
                                    ];
                                })}

                            </tr>
                        </tfoot>
                    </table>
                </Box>
            </Box>
            <Box sx={{ mt: 1, mr: 1, display: "flex", justifyContent: "space-between" }}>
                <Button sx={{ ml: 1 }}
                    text='Thoát'
                    size='medium'
                    leadingVisual={SignInIcon}
                    onClick={onCancel}
                />
                <Box
                    sx={{ mr: 1, display: 'flex', justifyContent: 'flex-end' }}
                ><Button
                        text="Export"
                        sx={{ mr: 2 }}
                        size="medium"
                        disabled={tinhBaoPhiKhoanResponses == undefined}
                        leadingVisual={DownloadIcon}
                        onClick={handleExportTableToExcel}
                    />
                    <Button
                        text="Xem thông báo phí"
                        sx={{ mr: 2 }}
                        size="medium"
                        disabled={tinhBaoPhiKhoanResponses == undefined}
                        leadingVisual={FileDiffIcon}
                        onClick={handleViewPdf}
                    />
                    <Button
                        text="Quay lại"
                        sx={{ mr: 2 }}
                        size="medium"
                        leadingVisual={ArrowLeftIcon}
                        onClick={onBack}
                    />
                    <Button
                        text="Tiếp tục"
                        trailingVisual={ArrowRightIcon}
                        variant="primary"
                        size="medium"
                        onClick={onSubmit}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default KetQuaBaoPhi;
